import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiLock, FiPlay, FiCpu } from 'react-icons/fi';
import { currentPath } from '../data/mockData';

export default function SkillMap() {
  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold mb-2">Skill Trajectory</h1>
        <p className="text-gray-400 font-mono">{currentPath.title}</p>
      </div>

      <div className="relative">
        {/* Connecting Line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-navy-800 -translate-x-1/2 rounded-full overflow-hidden">
           <div 
             className="w-full bg-quest-500 shadow-glow-green" 
             style={{ height: `${currentPath.progress}%` }}
           />
        </div>

        <div className="space-y-16 relative z-10">
          {currentPath.nodes.map((node, index) => {
            const isLeft = index % 2 === 0;
            const isCompleted = node.status === 'completed';
            const isCurrent = node.status === 'current';
            const isLocked = node.status === 'locked';

            return (
              <motion.div 
                key={node.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center justify-between w-full ${isLeft ? 'flex-row-reverse' : ''}`}
              >
                {/* Empty space for alternating layout */}
                <div className="w-5/12" />

                {/* Node Icon */}
                <div className="w-2/12 flex justify-center relative">
                  <div className={`
                    w-14 h-14 rounded-full flex items-center justify-center border-4 z-10 relative
                    ${isCompleted ? 'bg-navy-900 border-quest-500 text-quest-500' : 
                      isCurrent ? 'bg-quest-500 border-navy-900 text-navy-950 shadow-glow-green scale-110' : 
                      'bg-navy-900 border-navy-700 text-gray-600'}
                  `}>
                    {isCompleted && <SafeIcon icon={FiCheck} className="text-2xl" />}
                    {isCurrent && <SafeIcon icon={FiPlay} className="text-2xl ml-1" />}
                    {isLocked && <SafeIcon icon={FiLock} className="text-xl" />}
                  </div>
                  
                  {/* Pulse effect for current node */}
                  {isCurrent && (
                    <div className="absolute inset-0 rounded-full border-2 border-quest-500 animate-ping opacity-20" />
                  )}
                </div>

                {/* Node Content Card */}
                <div className={`w-5/12 ${isLeft ? 'text-right pr-8' : 'pl-8'}`}>
                  <div className={`
                    p-5 rounded-2xl border transition-all hover:-translate-y-1
                    ${isCompleted ? 'bg-navy-900/80 border-quest-500/30' : 
                      isCurrent ? 'bg-navy-800 border-quest-500 shadow-[0_0_20px_rgba(0,229,115,0.15)] cursor-pointer' : 
                      'bg-navy-950/50 border-navy-800 opacity-50'}
                  `}>
                    <div className={`text-xs font-mono mb-2 uppercase flex items-center gap-2 ${isLeft ? 'justify-end' : ''} text-gray-500`}>
                      {node.type === 'boss' && <SafeIcon icon={FiCpu} className="text-neon-pink" />}
                      {node.type}
                    </div>
                    <h3 className={`text-lg font-bold ${isLocked ? 'text-gray-500' : 'text-white'}`}>
                      {node.title}
                    </h3>
                    {isCurrent && (
                      <button className="mt-4 px-4 py-1.5 rounded-lg bg-quest-500/10 text-quest-400 border border-quest-500/50 text-sm font-medium hover:bg-quest-500 hover:text-navy-950 transition-colors">
                        Initialize
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}